import {
  useSelectedApPayee,
  useSelectedApPayeeForeignCurrency,
} from '@/components/ApPayee/useChangeApPayee';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useTpmDataFromBusinessPersonData } from '@/context/BusinessPersonData';
import { getInvoiceKindCategory } from '@/context/services/invoice_kind/InvoiceKind.service';
import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { useTransactionProperties } from '@/features/InvoiceReport/Edit/components/Context/API';
import { useIsLoadingWithAllApi } from '@/features/InvoiceReport/Edit/components/Context/ComponentAPI';
import { CsvImportDrawer } from '@/features/InvoiceReport/Edit/components/Drawer/CsvImportDrawer';
import { ActionsProvider } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Actions/Context';
import { useInvoiceTransactionHelper } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/hooks';
import { ExportAction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/ExportAction/ExportAction';
import { useGetExItem } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useGetExItem';
import { ConvertInvoiceTransaction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/model';
import { shouldHideTransactionPropertyColumn } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/utils/shouldHideTransactionPropertyColumn';
import { UtilizePastDetails } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails';
import {
  useHasSpecialExceptionWithControl,
  useReportForm,
} from '@/features/InvoiceReport/Edit/components/hooks/hooks';
import { useInvoiceReportNavigation } from '@/features/InvoiceReport/Edit/components/hooks/useGetInvoiceReportPath';
import type { InvoiceTransactionErrorType } from '@/features/InvoiceReport/Edit/components/type';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Checkbox,
  IconAdd,
  Skeleton,
  Table,
  TableEmptyType,
  randomId,
} from '@moneyforward/ap-frontend-components';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { Virtualizer, notUndefined } from '@tanstack/react-virtual';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import classnames from 'classnames/bind';
import { toISODateString } from 'date-util';
import dayjs, { type Dayjs } from 'dayjs';
import {
  ComponentProps,
  FC,
  MutableRefObject,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  Control,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayInsert,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  get,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import type { InvoiceTransaction, PaymentRequestForm } from '../../type.d';
import { UseColumnsOption, useColumns } from './Columns';
import styles from './InvoiceTransaction.module.scss';

export { Header as InvoiceTransactionHeader } from './Header/Header';

const cx = classnames.bind(styles);

export type Props = {
  control: Control<PaymentRequestForm, unknown>;
  fields: FieldArrayWithId<PaymentRequestForm, 'invoiceTransactions', 'id'>[];
  append: UseFieldArrayAppend<PaymentRequestForm, 'invoiceTransactions'>;
  remove: UseFieldArrayRemove;
  replace: UseFieldArrayReplace<PaymentRequestForm, 'invoiceTransactions'>;
  insert: UseFieldArrayInsert<PaymentRequestForm, 'invoiceTransactions'>;
  bookDate: Dayjs | null;
  isPermitMinusTransaction: boolean;
  includeTax: boolean;
  onChangeIncludeTax: (e: CheckboxChangeEvent) => void;
  showColumnDetails: boolean;
  onChangeShowColumnDetails: (e: CheckboxChangeEvent) => void;
  canDisplayDrExcise: boolean;
  virtualContainerRef: MutableRefObject<HTMLDivElement | null>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  isDutyFree: boolean;
  yScroll?: boolean;
  maxHeight?: number;
  hasWithholdingIncomeTax: boolean;
  invoiceTransactionRowErrors?: InvoiceTransactionErrorType | undefined;
};

export const InvoiceTransactionTable: FC<Props> = memo(
  ({
    control,
    fields,
    append,
    remove,
    replace,
    insert,
    yScroll,
    maxHeight,
    bookDate,
    isPermitMinusTransaction,
    includeTax,
    onChangeIncludeTax,
    showColumnDetails,
    onChangeShowColumnDetails,
    canDisplayDrExcise,
    isDutyFree,
    virtualContainerRef,
    rowVirtualizer,
    hasWithholdingIncomeTax,
    invoiceTransactionRowErrors,
  }) => {
    const isInvoiceKindUnqualifiedSeventyFlag = useBooleanFlagValue(
      'invoice-kind-unqualified-seventy',
      false
    );
    const {
      watch,
      resetField,
      formState: { errors },
      getValues,
      getFieldState,
    } = useFormContext<PaymentRequestForm>();
    const { append: addDeleteRecord } = useFieldArray<
      PaymentRequestForm,
      'deleteInvoiceTransactions'
    >({
      control: control,
      name: 'deleteInvoiceTransactions',
    });
    const watchFields = watch('invoiceTransactions');
    const { isApproverRoleFromPath } = useInvoiceReportNavigation();
    const { t } = useTranslation();
    const isLoading = useIsLoadingWithAllApi();
    const selectedPayee = useSelectedApPayee();
    const hasSpecialException = useHasSpecialExceptionWithControl(control);
    const { getDrExciseFromExItem } = useGetExItem();
    const { getInvoiceKindValue } = useReportForm();
    const tpmData = useTpmDataFromBusinessPersonData();
    const columnOptions: UseColumnsOption = useMemo(
      () => ({
        isTaxIncluded: includeTax,
        canDisplayTransactionDetail: showColumnDetails,
        canDisplayDrExcise: canDisplayDrExcise,
        isPermitMinusTransaction,
        hasWithholdingIncomeTax,
        hasSpecialException,
        isDutyFree,
        invoiceTransactionRowErrors,
      }),
      [
        includeTax,
        showColumnDetails,
        canDisplayDrExcise,
        isPermitMinusTransaction,
        hasWithholdingIncomeTax,
        hasSpecialException,
        isDutyFree,
        invoiceTransactionRowErrors,
      ]
    );
    const columns = useColumns(control, fields, columnOptions);
    const selectedForeignCurrency = useSelectedApPayeeForeignCurrency();
    const defaultInvoiceTransaction = useCallback(() => {
      const defaultExItemId = selectedPayee?.default_ex_item?.id;
      const drExcise = defaultExItemId
        ? getDrExciseFromExItem(defaultExItemId)
        : undefined;
      const dealDate = selectedPayee?.book_date
        ? dayjs(selectedPayee?.book_date)
        : bookDate;
      const invoiceKind = getInvoiceKindValue(
        hasSpecialException,
        isDutyFree,
        tpmData,
        drExcise?.id,
        toISODateString(dealDate || '')
      );

      return ConvertInvoiceTransaction({
        ...defaultValue,
        dealDate: dealDate,
        name: selectedPayee?.default_name ?? '',
        crItemId: selectedPayee?.default_cr_item?.id,
        crItemName: selectedPayee?.default_cr_item?.display_name,
        crSubItemId: selectedPayee?.default_cr_sub_item?.id,
        crSubItemName: selectedPayee?.default_cr_sub_item?.display_name,
        deptId: selectedPayee?.default_dept?.id,
        deptName: selectedPayee?.default_dept?.display_name,
        exItemId: defaultExItemId,
        exItemName: selectedPayee?.default_ex_item?.display_name,
        drExciseId: drExcise?.id,
        drExciseName: drExcise?.display_name,
        invoiceKind: invoiceKind,
        projectCodeId: selectedPayee?.default_project_code?.id,
        projectCodeName: selectedPayee?.default_project_code?.display_name,
        currency: selectedForeignCurrency.currency,
        jpyRate: selectedForeignCurrency.rate ?? 1,
        rowKey: randomId('invoice-transaction', 8),
        hasWithholdingIncomeTax: hasWithholdingIncomeTax,
      });
    }, [
      bookDate,
      getDrExciseFromExItem,
      getInvoiceKindValue,
      hasSpecialException,
      hasWithholdingIncomeTax,
      isDutyFree,
      selectedForeignCurrency.currency,
      selectedForeignCurrency.rate,
      selectedPayee?.book_date,
      selectedPayee?.default_cr_item?.display_name,
      selectedPayee?.default_cr_item?.id,
      selectedPayee?.default_cr_sub_item?.display_name,
      selectedPayee?.default_cr_sub_item?.id,
      selectedPayee?.default_dept?.display_name,
      selectedPayee?.default_dept?.id,
      selectedPayee?.default_ex_item?.display_name,
      selectedPayee?.default_ex_item?.id,
      selectedPayee?.default_name,
      selectedPayee?.default_project_code?.display_name,
      selectedPayee?.default_project_code?.id,
      tpmData,
    ]);
    const onAdd = useCallback(() => {
      append({
        ...defaultInvoiceTransaction(),
      });
    }, [append, defaultInvoiceTransaction]);
    const onCopy = useCallback(
      (index: number) => {
        const value: InvoiceTransaction = {
          ...watchFields[index]!,
          id: '',
          number: INVOICE_NUMBER_UNSAVED,
          rowKey: randomId('invoice-transaction', 8),
        };
        insert(index, { ...value }, { shouldFocus: false });
      },
      [watchFields, insert]
    );
    const onAddWithTop = useCallback(
      (index: number) => {
        insert(index, {
          ...defaultInvoiceTransaction(),
        });
      },
      [insert, defaultInvoiceTransaction]
    );
    const onAddWithBottom = useCallback(
      (index: number) => {
        insert(index + 1, {
          ...defaultInvoiceTransaction(),
        });
      },
      [insert, defaultInvoiceTransaction]
    );
    const onDelete = useCallback(
      (index: number) => {
        const item = watchFields[index];
        if (window.confirm('選択した明細を削除してもよろしいですか？')) {
          remove(index);
          if (item && item.id && item.number !== INVOICE_NUMBER_UNSAVED) {
            addDeleteRecord({ id: item.id });
          }
        }
      },
      [addDeleteRecord, remove, watchFields]
    );
    useEffect(() => {
      const { isDirty } = getFieldState('payee.value');
      if (selectedPayee && !selectedForeignCurrency.isLoading && isDirty) {
        const invoiceTransaction = getValues('invoiceTransactions');
        if (invoiceTransaction.length === 0) {
          onAdd();
        }
      }
    }, [
      getFieldState,
      getValues,
      onAdd,
      selectedForeignCurrency.isLoading,
      selectedPayee,
    ]);

    const { dealDateHelper, updateExciseValue } = useInvoiceTransactionHelper(
      includeTax,
      showColumnDetails,
      canDisplayDrExcise,
      hasSpecialException,
      isDutyFree,
      tpmData
    );

    const onImport = useCallback(
      async (items: InvoiceTransaction[]) => {
        return new Promise<void>((resolve) => {
          watchFields.forEach((item) => {
            const id = item.id;
            if (id && !id.startsWith('imp')) addDeleteRecord({ id });
          });
          resetField('invoiceTransactions', { defaultValue: [] });
          items.forEach((item) => {
            const inputInvoiceKind = item.invoiceKind;
            // NOTE: hasSpecialExceptionとtpmData を引数で渡しても結局同じなので渡さない
            dealDateHelper(item);
            // Override invoice kind for CSV import in /approving_reports and /admin_invoice_reports
            // Ensure that the `inputInvoiceKind` matches with the tax category
            const inputCategory = getInvoiceKindCategory(inputInvoiceKind);
            const autoDetectedCategory = getInvoiceKindCategory(
              item.invoiceKind
            );
            if (
              isInvoiceKindUnqualifiedSeventyFlag &&
              inputInvoiceKind !== undefined &&
              inputInvoiceKind !== '' &&
              inputCategory === autoDetectedCategory
            ) {
              item.invoiceKind = inputInvoiceKind;
            }
          });
          replace(items);
          resolve();
        });
      },
      [
        addDeleteRecord,
        dealDateHelper,
        replace,
        resetField,
        watchFields,
        isInvoiceKindUnqualifiedSeventyFlag,
      ]
    );

    const transactionProperties = useTransactionProperties();
    const { currentOffice } = useLoginInfo();

    const exactHasCustomTransactionKey = useMemo(
      () =>
        new Set(
          watchFields?.flatMap((invoiceTransaction) =>
            Object.keys(invoiceTransaction.custom_transaction_items ?? {})
          ) ?? []
        ),
      [watchFields]
    );

    //Hide columns based on plan and visibility
    const filteredTransactionProperties = useMemo(
      () =>
        transactionProperties?.filter(
          (prop) =>
            !shouldHideTransactionPropertyColumn(
              prop,
              !!currentOffice?.isMoreThanTeamPlan,
              exactHasCustomTransactionKey.has(prop.id)
            )
        ),
      [
        transactionProperties,
        exactHasCustomTransactionKey,
        currentOffice?.isMoreThanTeamPlan,
      ]
    );

    const pagination = useMemo(() => ({ maxPage: 1 }), []);
    const TableAction = useMemo(
      () => (
        <div className={cx(styles['table-actions'])}>
          <div className={cx(styles['checkbox-group'])}>
            <Checkbox
              checked={includeTax}
              onChange={onChangeIncludeTax}
              label='税込金額で入力'
            />
            <Checkbox
              checked={showColumnDetails}
              onChange={onChangeShowColumnDetails}
              label='詳細表示(単価、数量、メモ欄)'
            />
          </div>
          <ButtonGroup>
            <ExportAction
              watchFields={watchFields}
              includeTax={includeTax}
              showColumnDetails={showColumnDetails}
              isApproverRoleFromPath={isApproverRoleFromPath}
              hasWithholdingIncomeTax={hasWithholdingIncomeTax}
              canDisplayDrExcise={canDisplayDrExcise}
              transactionProperties={filteredTransactionProperties}
            />
            <CsvImportDrawer
              onImport={onImport}
              invoiceTransactionDetails={showColumnDetails}
              taxInclude={includeTax}
              hasWithholdingIncomeTax={hasWithholdingIncomeTax}
              canDisplayDrExcise={canDisplayDrExcise}
              isAdminOrApproval={isApproverRoleFromPath}
              updateExciseValue={updateExciseValue}
              transactionProperties={filteredTransactionProperties}
            />
            <UtilizePastDetails
              selectedPayee={selectedPayee}
              onImport={onImport}
              showColumnDetails={showColumnDetails}
              canDisplayDrExcise={canDisplayDrExcise}
            />
            <ButtonV2
              isSecondary
              onClick={onAdd}
              leftIcon={<IconAdd size={16} />}
              isLoading={selectedForeignCurrency.isLoading}
            >
              {t('payment_request_transaction_add')}
            </ButtonV2>
          </ButtonGroup>
        </div>
      ),
      [
        includeTax,
        onChangeIncludeTax,
        showColumnDetails,
        onChangeShowColumnDetails,
        watchFields,
        isApproverRoleFromPath,
        hasWithholdingIncomeTax,
        canDisplayDrExcise,
        filteredTransactionProperties,
        onImport,
        updateExciseValue,
        selectedPayee,
        onAdd,
        selectedForeignCurrency.isLoading,
        t,
      ]
    );
    const memorizedParagraph = useMemo(() => ({ rows: 4, width: '100%' }), []);
    const loadingProps:
      | { emptyText: JSX.Element; emptyType: TableEmptyType }
      | {} = useMemo(
      () =>
        isLoading
          ? {
              emptyText: (
                <Skeleton title={false} active paragraph={memorizedParagraph} />
              ),
              emptyType: 'loading' satisfies TableEmptyType,
            }
          : {
              emptyType: null,
              emptyText: t('payment_request_transaction_empty_text'),
            },
      [isLoading, memorizedParagraph, t]
    );
    const rowHeight = 45;
    const virtualItems = rowVirtualizer.getVirtualItems();
    const items = virtualItems.map((item) =>
      getValues(`invoiceTransactions.${item.index}`)
    );
    const [before, after] =
      items.length > 0
        ? [
            notUndefined(virtualItems[0]).start -
              rowVirtualizer.options.scrollMargin,
            rowVirtualizer.getTotalSize() -
              notUndefined(virtualItems[virtualItems.length - 1]).end,
          ]
        : [0, 0];
    const components: ComponentProps<
      typeof Table<InvoiceTransaction>
    >['components'] = useMemo(
      () => ({
        body: {
          wrapper: ({
            className,
            children,
          }: {
            className: string;
            children: ReactNode;
          }) => {
            const beforeStyle = { height: before };
            const afterStyle = { height: after };
            // see: https://github.com/TanStack/virtual/issues/585#issuecomment-1716173260
            return (
              <tbody className={`${className} virtual-body`}>
                {before > 0 && (
                  <tr>
                    <td colSpan={columns.length} style={beforeStyle} />
                  </tr>
                )}
                {children}
                {after > 0 && (
                  <tr>
                    <td colSpan={columns.length} style={afterStyle} />
                  </tr>
                )}
              </tbody>
            );
          },
          row: ({
            children,
            ...props
          }: {
            children: ReactNode;
            'data-row-key': string;
            className: string;
          }) => {
            const field = fields.find(
              (item) => item.rowKey === props['data-row-key']
            );
            if (!field) return undefined;
            const index = fields.indexOf(field);
            const virtualItem = virtualItems.find(
              (item) => item.index === index
            );
            if (!virtualItem) {
              return null;
            }
            const error = get(errors, `invoiceTransactions.${index}.rowKey`);
            const isError = Boolean(error);
            return (
              <tr
                {...props}
                className={cx(props.className, 'virtual-row')}
                key={`${field?.rowKey}_${isError}`}
                data-row-key={`${field?.rowKey}_${isError}`}
                aria-invalid={isError}
              >
                {children}
              </tr>
            );
          },
        },
      }),
      [after, before, columns.length, errors, fields, virtualItems]
    );
    const visibleRowCount = 3;
    const scrollY = yScroll
      ? rowHeight * visibleRowCount
      : (maxHeight || window.innerHeight) - 48;
    const tableHeaderHeight = 31;

    return (
      <div className={cx(styles['container'])}>
        <ActionsProvider
          onCopy={onCopy}
          onAddWithTop={onAddWithTop}
          onAddWithBottom={onAddWithBottom}
          onDelete={onDelete}
        >
          <div>{TableAction}</div>
          <div
            ref={virtualContainerRef}
            // eslint-disable-next-line @moneyforward/account-payable/literals-in-props
            style={{
              height: `${scrollY + tableHeaderHeight}px`,
              overflowY: 'auto',
            }}
          >
            <div
              // eslint-disable-next-line @moneyforward/account-payable/literals-in-props
              style={{
                height:
                  watchFields.length > 0
                    ? `${Math.max(rowVirtualizer.getTotalSize(), scrollY)}px`
                    : undefined,
              }}
            >
              <Table<InvoiceTransaction>
                legacy={false}
                rowKey='rowKey'
                columns={columns}
                data={items}
                pagination={pagination}
                resizable
                tableLayout='fixed'
                components={components}
                {...loadingProps}
              />
            </div>
          </div>
        </ActionsProvider>
      </div>
    );
  }
);
InvoiceTransactionTable.displayName = 'InvoiceTransactionTable';

const defaultValue: Omit<InvoiceTransaction, 'rowKey' | 'isCurrencyJPY'> = {
  id: '',
  number: INVOICE_NUMBER_UNSAVED,
  dealDate: null,
  name: undefined,
  exItemId: undefined,
  unit: undefined,
  quantity: 1,
  totalValue: undefined,
  drExciseId: undefined,
  invoiceKind: undefined,
  exciseValue: undefined,
  hasWithholdingIncomeTax: undefined,
  withholdingIncomeTax: undefined,
  memo: undefined,
  deptId: undefined,
  projectCodeId: undefined,
  currency: 'JPY',
  useCustomJPYRate: undefined,
  jpyRate: 1,
  crItemId: undefined,
  crSubItemId: undefined,
  taxIncludedTotalValue: 0,
  taxIncludedUnit: 0,
};
